import { jwtDecode } from 'jwt-decode';

/**
 * Utilidades para manejar el claim `custom:rol` del JWT.
 *
 * Cognito puede entregar uno o varios roles en el mismo claim separados por
 * comas (ej: "admin,cx"). Estas funciones normalizan ese valor a una lista de
 * roles para que toda la app razone de la misma forma, sin importar si el
 * usuario tiene uno o varios roles.
 */

/** Nombre del claim que contiene el/los rol(es) del usuario. */
const ROLE_CLAIM = 'custom:rol';

/**
 * Convierte el valor crudo del claim en una lista de roles normalizados
 * (sin espacios, en minúscula y sin entradas vacías).
 *
 * Acepta tanto un string separado por comas como un arreglo de strings.
 */
export function parseRoles(rawRole: unknown): string[] {
  if (!rawRole) return [];

  const parts = Array.isArray(rawRole)
    ? rawRole
    : String(rawRole).split(',');

  return parts
    .map(role => role.trim().toLowerCase())
    .filter(role => role.length > 0);
}

/**
 * Decodifica el token y retorna la lista de roles del usuario.
 * Retorna [] si no hay token, no se puede decodificar o no trae roles.
 */
export function getRolesFromToken(token: string | null): string[] {
  if (!token) return [];
  try {
    const decoded: any = jwtDecode(token);
    return parseRoles(decoded[ROLE_CLAIM]);
  } catch {
    return [];
  }
}

/** Lee el token de localStorage y retorna la lista de roles del usuario. */
export function getUserRoles(): string[] {
  return getRolesFromToken(localStorage.getItem('authToken'));
}

/** True si el usuario tiene al menos uno de los roles permitidos. */
export function hasAnyRole(userRoles: string[], allowedRoles: string[]): boolean {
  if (!allowedRoles?.length) return false;
  const allowed = allowedRoles.map(r => r.toLowerCase());
  return userRoles.some(role => allowed.includes(role));
}

/** True si el usuario tiene el rol indicado. */
export function hasRole(userRoles: string[], role: string): boolean {
  return userRoles.includes(role.toLowerCase());
}
